import { useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, FileSpreadsheet, FileUp, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { parseGpc, type GpcStatement } from "@/lib/gpc/gpcParser";
import { csvFileName, gpcSheetRows, gpcToCsv } from "@/lib/gpc/gpcToCsv";
import { localePath, makeTr, type LandingLocale } from "@/lib/landing/locale";
import { isWithinFileCap } from "@/lib/tools/limits";
import { track } from "@/lib/tracking/analytics";
import { cn } from "@/lib/utils";

/**
 * The free GPC → CSV converter. Everything happens in this browser tab: the
 * file is read with `File.arrayBuffer()`, parsed by the app's own GPC parser
 * and turned into a CSV (or, with SheetJS loaded on demand, an XLSX) download
 * through a Blob URL. No request, no storage. After consent, one `tool_used`
 * event says which format was downloaded; nothing from the file goes with it.
 *
 * Prerender-safe: nothing touches window or document until the visitor picks
 * a file.
 */

const PREVIEW_ROWS = 8;

type State =
  | { kind: "idle" }
  | { kind: "error"; message: string }
  | { kind: "done"; fileName: string; statements: GpcStatement[] };

function formatAmount(amount: number, locale: LandingLocale): string {
  return new Intl.NumberFormat(locale === "cs" ? "cs-CZ" : "en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function GpcConverter({ locale }: { locale: LandingLocale }) {
  const tr = makeTr(locale);
  const inputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<State>({ kind: "idle" });
  const [dragging, setDragging] = useState(false);

  async function readFile(file: File) {
    // Larger than any real statement; within it the parser's own line cap cannot be reached.
    if (!isWithinFileCap(file.size)) {
      setState({
        kind: "error",
        message: tr(
          "Soubor je na výpis GPC příliš velký (víc než 10 MB).",
          "The file is too large for a GPC statement (over 10 MB)."
        ),
      });
      return;
    }
    try {
      const statements = parseGpc(await file.arrayBuffer());
      if (statements.length === 0) throw new Error("no statements");
      setDownloaded(false);
      setState({ kind: "done", fileName: file.name, statements });
    } catch {
      setState({
        kind: "error",
        message: tr(
          "Tohle nevypadá jako výpis GPC (ABO). Zkontrolujte, že jste ve Fio stáhli výpis ve formátu GPC.",
          "This doesn't look like a GPC (ABO) statement. Check that you downloaded the statement from Fio in GPC format."
        ),
      });
    }
  }

  function onPick(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) void readFile(file);
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) void readFile(file);
  }

  const [downloaded, setDownloaded] = useState(false);

  function save(blob: Blob, fileName: string, output: "csv" | "csv_alt" | "xlsx") {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
    setDownloaded(true);
    track("tool_used", { tool: "gpc_to_csv", output });
  }

  function download(statements: GpcStatement[], dialect: LandingLocale) {
    const blob = new Blob([gpcToCsv(statements, dialect)], { type: "text/csv;charset=utf-8" });
    save(blob, csvFileName(statements, dialect), dialect === locale ? "csv" : "csv_alt");
  }

  async function downloadXlsx(statements: GpcStatement[]) {
    try {
      // SheetJS is large: load it only when someone asks for Excel.
      const XLSX = await import("xlsx");
      const sheet = XLSX.utils.aoa_to_sheet(gpcSheetRows(statements, locale));
      sheet["!cols"] = [14, 12, 12, 18, 10, 32, 14, 14, 14, 16].map((wch) => ({ wch }));
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, tr("Výpis", "Statement"));
      const bytes = XLSX.write(book, { type: "array", bookType: "xlsx" });
      const blob = new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      save(blob, csvFileName(statements, locale, "xlsx"), "xlsx");
    } catch {
      setState({
        kind: "error",
        message: tr(
          "Soubor pro Excel se nepodařilo vytvořit. Zkuste stáhnout CSV.",
          "The Excel file could not be created. Try the CSV download."
        ),
      });
    }
  }

  const items = state.kind === "done" ? state.statements.flatMap((s) => s.items) : [];
  const warnings = state.kind === "done" ? state.statements.flatMap((s) => s.warnings) : [];

  return (
    <div className="rounded-xl border bg-card p-5 sm:p-6">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cn(
          "flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed px-4 py-10 text-center transition-colors",
          dragging && "border-[hsl(var(--action))] bg-primary/10 dark:border-primary"
        )}
      >
        <FileUp aria-hidden="true" className="size-6 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          {tr(
            "Přetáhněte sem výpis .gpc, .abo nebo .txt, nebo",
            "Drop a .gpc, .abo or .txt statement here, or"
          )}
        </p>
        <Button type="button" onClick={() => inputRef.current?.click()}>
          {tr("Vybrat soubor", "Choose a file")}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept=".gpc,.abo,.txt"
          className="sr-only"
          aria-label={tr("Výpis GPC (ABO)", "GPC (ABO) statement")}
          onChange={onPick}
        />
      </div>

      <p className="mt-4 flex items-start gap-2 text-sm text-muted-foreground">
        <ShieldCheck
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0 text-[hsl(var(--action))] dark:text-primary"
        />
        {tr(
          "Soubor se nikam neodesílá: převod proběhne jen ve vašem prohlížeči a nic se neukládá.",
          "The file is never sent anywhere: the conversion runs only in your browser and nothing is stored."
        )}
      </p>

      <div aria-live="polite">
        {state.kind === "error" && (
          <p className="mt-5 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm">
            {state.message}
          </p>
        )}

        {state.kind === "done" && (
          <div className="mt-6">
            <p className="text-sm">
              <span className="font-medium">{state.fileName}</span>
              {" · "}
              {tr(
                `výpisů: ${state.statements.length}, pohybů: ${items.length}`,
                `statements: ${state.statements.length}, movements: ${items.length}`
              )}
            </p>
            {warnings.length > 0 && (
              <p className="mt-2 text-sm text-muted-foreground">
                {tr(
                  `Výpis má ${warnings.length} upozornění (např. nesedící zůstatky nebo nepodporované záznamy); pohyby jsou v CSV i tak.`,
                  `The statement has ${warnings.length} warning(s) (e.g. balances that don't add up or unsupported records); the movements are in the CSV anyway.`
                )}
              </p>
            )}

            <div className="mt-4 overflow-x-auto rounded-lg border">
              <table className="w-full min-w-[32rem] text-left text-sm">
                <thead className="border-b bg-secondary/40 text-xs text-muted-foreground">
                  <tr>
                    <th scope="col" className="px-3 py-2 font-medium">
                      {tr("Datum", "Date")}
                    </th>
                    <th scope="col" className="px-3 py-2 text-right font-medium">
                      {tr("Částka", "Amount")}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      {tr("Protistrana", "Counterparty")}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      VS
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.slice(0, PREVIEW_ROWS).map((item, i) => (
                    <tr key={`${item.documentId}-${i}`}>
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                        {item.postingDate}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                        {formatAmount(item.amount, locale)}
                      </td>
                      <td className="px-3 py-2">{item.counterName || "—"}</td>
                      <td className="px-3 py-2 tabular-nums">{item.variableSymbol ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {items.length > PREVIEW_ROWS && (
              <p className="mt-2 text-xs text-muted-foreground">
                {tr(
                  `Náhled prvních ${PREVIEW_ROWS} z ${items.length} pohybů; v CSV jsou všechny.`,
                  `Preview of the first ${PREVIEW_ROWS} of ${items.length} movements; the CSV has them all.`
                )}
              </p>
            )}

            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Button type="button" onClick={() => download(state.statements, locale)}>
                <Download aria-hidden="true" className="mr-2 size-4" />
                {tr("Stáhnout CSV", "Download CSV")}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => void downloadXlsx(state.statements)}
              >
                <FileSpreadsheet aria-hidden="true" className="mr-2 size-4" />
                {tr("Stáhnout Excel (XLSX)", "Download Excel (XLSX)")}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => download(state.statements, locale === "cs" ? "en" : "cs")}
              >
                {tr("CSV s desetinnou tečkou a čárkami", "CSV with decimal comma and semicolons")}
              </Button>
            </div>

            {downloaded && (
              <p className="mt-6 rounded-lg border bg-secondary/40 px-4 py-3 text-sm leading-relaxed">
                {tr(
                  "Chcete platby z Fio stahovat automaticky každý den a párovat je s doklady? ",
                  "Want your Fio payments downloaded automatically every day and matched with your documents? "
                )}
                <Link
                  to={localePath(locale, "/")}
                  className="inline-flex items-center gap-1 font-medium text-[hsl(var(--action))] underline-offset-4 hover:underline dark:text-primary"
                >
                  {tr("Podívejte se na Zettl", "Take a look at Zettl")}
                  <ArrowRight aria-hidden="true" className="size-3.5" />
                </Link>
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
